// Rep "My clients" — the accounts in the rep's book, most urgent first (on hold → overdue → recent).
// Find a client, see what they owe and what's overdue, start an order, or jump to their orders,
// payments and statement.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import { fetchRepBook, clientStatementUrl } from '../api/me.js';
import { fmtMoney } from '../lib/matrix.js';
import { storeShort } from '../lib/stores.js';

const money2 = (obj) =>
  Object.entries(obj || {})
    .filter(([, v]) => v > 0)
    .map(([c, v]) => fmtMoney(v, c))
    .join(' · ');

export default function RepClients() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    fetchRepBook()
      .then(setCompanies)
      .catch(() => setCompanies([]));
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!companies || !needle) return companies || [];
    return companies.filter((co) =>
      [co.name, ...co.clients.flatMap((c) => [c.clientName, c.clientEmail, c.locationName])].filter(Boolean).some((s) => s.toLowerCase().includes(needle))
    );
  }, [companies, q]);

  // Hand the chosen client to New Order via router state (ephemeral — nothing persists).
  const startOrder = (c) => navigate('/catalog', { state: { client: c } });

  if (!companies) return <div className="text-sm text-text-muted">Loading…</div>;

  const link = 'rounded-md border border-line px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-raised';

  return (
    <>
      <PageHeader
        title="My clients"
        subtitle="Clients that need attention are shown first. Amounts cover every order on the account, whoever placed it."
        actions={
          companies.length > 3 && (
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Find a client…"
              aria-label="Find a client"
              className="w-56 rounded-md border border-line px-3 py-1.5 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
            />
          )
        }
      />

      {companies.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">
          No clients assigned to you yet. Your TEAMM8 contact will assign your accounts.
        </div>
      ) : shown.length === 0 ? (
        <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">No clients match “{q}”.</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {shown.map((co) => {
            const overdue = money2(co.overdue);
            const owed = money2(co.outstanding);
            return (
              <div key={co.id} className={`rounded-lg border bg-surface p-4 shadow-sm ${co.onHold ? 'border-error/50' : overdue ? 'border-warning/50' : 'border-line'}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-semibold text-text-primary">{co.name}</h3>
                  <span className="rounded bg-surface-raised px-1.5 py-0.5 text-[10px] font-medium text-text-muted">{storeShort(co.store)}</span>
                  {co.onHold && (
                    <span className="rounded-full bg-error px-2 py-0.5 text-[10px] font-semibold text-white" title={co.holdReason || ''}>
                      On hold
                    </span>
                  )}
                  {!co.onHold && overdue && <span className="rounded-full bg-warning-light px-2 py-0.5 text-[10px] font-semibold text-warning">Overdue</span>}
                  {co.dormant && (
                    <span className="rounded-full bg-surface-raised px-2 py-0.5 text-[10px] font-medium text-text-muted" title={`No order in the last ${co.dormantDays} days`}>
                      No order in {co.dormantDays}+ days
                    </span>
                  )}
                </div>
                {co.onHold && co.holdReason && <p className="mt-1 text-[11px] text-error">{co.holdReason}. New orders need TEAMM8 approval — chase payment first.</p>}

                <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <dt className="text-text-muted">Orders</dt>
                    <dd className="text-text-primary">{co.orderCount}</dd>
                  </div>
                  <div>
                    <dt className="text-text-muted">Last order</dt>
                    <dd className="text-text-primary">{co.lastOrderAt ? new Date(co.lastOrderAt).toLocaleDateString() : '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-text-muted">Owed</dt>
                    <dd className="text-text-primary">{owed || '—'}</dd>
                    {overdue && <dd className="font-medium text-error">{overdue} overdue</dd>}
                  </div>
                </dl>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  <button onClick={() => navigate(`/orders?companyId=${co.id}`)} className={link}>
                    Orders
                  </button>
                  <button onClick={() => navigate(`/payments?companyId=${co.id}`)} className={link}>
                    Payments
                  </button>
                  <a href={clientStatementUrl(co.id)} target="_blank" rel="noreferrer" className={link}>
                    Statement PDF
                  </a>
                </div>

                <div className="mt-3 space-y-1.5">
                  {co.clients.length === 0 ? (
                    <p className="rounded-md border border-dashed border-line px-2.5 py-2 text-xs text-text-muted">No store set up for this client yet.</p>
                  ) : (
                    co.clients.map((c) => {
                      const key = c.onBehalfOfClientUserId || c.shopifyCompanyLocationId;
                      return (
                        <div key={key} className="flex items-center justify-between gap-2 rounded-md border border-line px-2.5 py-1.5">
                          <div className="min-w-0">
                            <div className="truncate text-sm text-text-primary">{c.locationName || co.name}</div>
                            <div className="truncate text-xs text-text-muted">
                              {c.clientName || c.clientEmail || 'No login yet — you can still order for them'}
                              {c.currencyCode ? ` · ${c.currencyCode}` : ''}
                            </div>
                          </div>
                          <button onClick={() => startOrder(c)} className="shrink-0 rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-hover">
                            New order
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
